import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import HeroBookingFlowOriginal from "@/components/HeroBookingFlowOriginal";
import "@/landing.css";
import { enableHeroScrollTakeover } from "@/lib/hero-scroll-takeover";
import { trackEvent } from "@/lib/posthog";
import { trackPixelEvent } from "@/lib/meta-pixel";
import { supabase } from "@/integrations/supabase/client";

function trackCta(cta_text: string, cta_location: string) {
  const entry_path = window.location.pathname;

  trackEvent('cta_clicked', { cta_text, cta_location, entry_path });
}

// ─── TYPES ────────────────────────────────────────────────────────────────────
type CSSWithVars = React.CSSProperties & { [key: `--${string}`]: string | number };

// ─── ICONS ────────────────────────────────────────────────────────────────────
const Icon = ({ children, className = "w-5 h-5", stroke = 1.6 }: {
  children: React.ReactNode; className?: string; stroke?: number;
}) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke}
       strokeLinecap="round" strokeLinejoin="round" className={className}>
    {children}
  </svg>
);

const ICheck   = (p: { className?: string; stroke?: number }) => <Icon {...p}><path d="M5 12.5l4 4 10-10"/></Icon>;
const IX       = (p: { className?: string }) => <Icon {...p}><path d="M6 6l12 12M18 6L6 18"/></Icon>;
const IArrow   = (p: { className?: string }) => <Icon {...p}><path d="M5 12h14M13 5l7 7-7 7"/></Icon>;
const IArrowDown = (p: { className?: string }) => <Icon {...p}><path d="M12 5v14M5 13l7 7 7-7"/></Icon>;
const ICalendar = (p: { className?: string }) => <Icon {...p}><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/></Icon>;
const ICard    = (p: { className?: string }) => <Icon {...p}><rect x="2.5" y="5.5" width="19" height="13" rx="2"/><path d="M2.5 10h19M6 15h4"/></Icon>;
const IChat    = (p: { className?: string }) => <Icon {...p}><path d="M4 5h16v11H8l-4 4V5z"/></Icon>;
const ITrend   = (p: { className?: string }) => <Icon {...p}><path d="M3 17l6-6 4 4 8-8"/><path d="M14 7h7v7"/></Icon>;
const IRepeat  = (p: { className?: string }) => <Icon {...p}><path d="M17 1l4 4-4 4"/><path d="M3 11V9a4 4 0 014-4h14"/><path d="M7 23l-4-4 4-4"/><path d="M21 13v2a4 4 0 01-4 4H3"/></Icon>;
const IPackage = (p: { className?: string }) => <Icon {...p}><path d="M21 16V8l-9-5-9 5v8l9 5 9-5z"/><path d="M3.3 7L12 12l8.7-5M12 22V12"/></Icon>;
const IZap     = (p: { className?: string }) => <Icon {...p}><path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z"/></Icon>;
const LogoApplePay = (p: { className?: string }) => <img src="/logos/applepay.svg" alt="Apple Pay" className={p.className} />;
const LogoGooglePay = (p: { className?: string }) => <img src="/logos/googlepay.svg" alt="Google Pay" className={p.className} />;

// ─── FIGGY LOGO ───────────────────────────────────────────────────────────────
const FiggyLogo = ({ darkText = false, wordmarkClassName }: { darkText?: boolean; wordmarkClassName?: string }) => (
  <div className="flex items-center gap-2">
    <img src="/uploads/figgy_icon_transparent.png" alt="Figgy" width={28} height={28} style={{ objectFit: "contain" }} />
    <span className={`font-display font-bold text-[1.55rem] tracking-tight ${wordmarkClassName ?? (darkText ? "text-ink-950" : "text-white")}`} style={{ lineHeight: 1 }}>Figgy</span>
  </div>
);

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const Container = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`max-w-[1240px] mx-auto px-6 md:px-8 ${className}`}>{children}</div>
);

// variant: "adaptive" follows the page theme (light base + dark: overrides),
// "ink" is always dark text (sections that stay white in both themes),
// "onColor" is always white text (the purple Make-money section).
const SectionLabel = ({ num, label, variant = "adaptive" }: { num: string; label: string; variant?: "adaptive" | "ink" | "onColor" }) => {
  const styles = {
    adaptive: { wrap: "text-ink-500 dark:text-white/50", num: "text-[#5048E5] dark:text-figgy-400", line: "bg-ink-300 dark:bg-white/20" },
    ink:      { wrap: "text-ink-500", num: "text-[#5048E5]", line: "bg-ink-300" },
    onColor:  { wrap: "text-white/55", num: "text-white", line: "bg-white/25" },
  }[variant];
  return (
    <div className={`flex items-center gap-3 mono text-[11px] uppercase tracking-[0.2em] ${styles.wrap}`}>
      <span className={`${styles.num} font-bold`}>{num}</span>
      <span className={`h-px w-8 ${styles.line}`} />
      <span>{label}</span>
    </div>
  );
};

// ─── BOOKING CARD (Midnight Luxe) ─────────────────────────────────────────────
const MIDNIGHT_LUXE = {
  gradientFrom: "#0f0f1a",
  accentColor:  "#3b82f6",
  textPrimary:  "#ffffff",
  textSecondary:"#9ca3af",
  borderColor:  "#1f2937",
};

const LuxeBookingCard = () => {
  const theme = MIDNIGHT_LUXE;
  const tiles = Array.from({ length: 48 }, (_, i) => i < 7);
  return (
    <div className="relative font-sans" style={{ width: 150, aspectRatio: "2 / 3" }}>
      <div className="w-full h-full rounded-2xl p-3 flex flex-col relative overflow-hidden"
           style={{ background: theme.gradientFrom, boxShadow: "0 2px 8px rgba(0,0,0,0.16), 0 12px 40px rgba(0,0,0,0.24), 0 0 1px rgba(0,0,0,0.2)" }}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0" style={{ maskImage: "radial-gradient(ellipse at center, transparent 25%, black 75%)", WebkitMaskImage: "radial-gradient(ellipse at center, transparent 25%, black 75%)" }}>
            <div className="grid grid-cols-6 gap-1 p-3 h-full content-center">
              {tiles.map((active, i) => (
                <div key={i} className="aspect-square rounded-[2px]"
                     style={{ backgroundColor: active ? theme.accentColor : theme.borderColor, opacity: active ? 0.22 : 0.21 }} />
              ))}
            </div>
          </div>
        </div>
        <div className="absolute inset-0 rounded-2xl pointer-events-none"
             style={{ boxShadow: "inset 0 2px 4px rgba(0,0,0,0.1), inset 0 -1px 2px rgba(255,255,255,0.05)" }} />
        <div className="relative flex items-start justify-between mb-1.5">
          <div className="flex-1 min-w-0 pr-1">
            <div className="flex items-center gap-1">
              <div className="font-bold tracking-tight leading-tight text-[9px] truncate" style={{ color: theme.textPrimary }}>Maya Cuts</div>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-2 h-2 flex-shrink-0" style={{ color: theme.textSecondary }}>
                <rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="0.5" fill="currentColor"/>
              </svg>
            </div>
          </div>
          <div className="flex flex-col items-end flex-shrink-0">
            <div className="flex items-center gap-0.5">
              <div className="font-bold tabular-nums leading-none text-[11px]" style={{ color: theme.textPrimary }}>7</div>
              <svg viewBox="0 0 24 24" className="w-2.5 h-2.5" fill={theme.accentColor}>
                <circle cx="12" cy="12" r="10"/>
                <path d="M9 12l2 2 4-4" stroke="#0f0f1a" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="uppercase tracking-wider font-medium mt-0.5" style={{ color: theme.textSecondary, fontSize: "6px" }}>VISITS</div>
          </div>
        </div>
        <div className="relative flex-1 flex items-center justify-center my-1">
          <div className="absolute font-black leading-none tracking-tighter blur-sm" style={{ color: theme.accentColor, fontFamily: "Satoshi, sans-serif", fontStyle: "italic", opacity: 0.15, transform: "translate(1px, 1px)", fontSize: "44px" }}>MC</div>
          <div className="relative font-black leading-none tracking-tighter" style={{ color: theme.accentColor, fontFamily: "Satoshi, sans-serif", fontStyle: "italic", opacity: 0.95, filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))", fontSize: "44px" }}>MC</div>
        </div>
        <div className="relative mt-auto flex justify-between items-end gap-1.5">
          <div className="flex-1 min-w-0">
            <div className="font-bold uppercase tracking-wide mb-0.5 text-[8px] truncate" style={{ color: theme.textPrimary }}>ALEX RIVERA</div>
            <div className="uppercase tracking-wider mb-0.5" style={{ color: theme.textSecondary, fontSize: "6px" }}>MEMBER SINCE: MAR 2024</div>
            <div className="flex items-center gap-0.5">
              <span className="font-bold leading-none text-[7px]" style={{ color: theme.textSecondary }}>#</span>
              <div className="font-medium tracking-wide text-[7px]" style={{ color: theme.textSecondary }}>FG-7K2M</div>
            </div>
          </div>
          <div className="font-medium tracking-wide text-right flex-shrink-0 uppercase" style={{ color: theme.textSecondary, fontSize: "6px" }}>MADE WITH FIGGY</div>
        </div>
      </div>
    </div>
  );
};

// ─── HERO FLOATS ──────────────────────────────────────────────────────────────
const FloatPayment = () => (
  <div className="rounded-2xl bg-white text-ink-900 shadow-2xl p-3 pr-4 flex items-center gap-3 border border-white/30">
    <div className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center font-black">$</div>
    <div>
      <div className="text-[11px] text-ink-500 mono uppercase tracking-wider">Booking confirmed</div>
      <div className="text-sm font-black">+ $65.00</div>
    </div>
  </div>
);

const FloatSMS = () => (
  <div className="rounded-2xl bg-emerald-100 text-emerald-900 shadow-2xl p-3 pr-4 flex items-center gap-3 border border-emerald-200/50">
    <IChat className="w-5 h-5 text-emerald-700" />
    <div>
      <div className="text-[11px] text-emerald-700 mono uppercase tracking-wider">SMS reminder sent</div>
      <div className="text-sm font-black text-emerald-900">Maya, see you tomorrow</div>
    </div>
  </div>
);

// ─── PHONE COMPONENTS ─────────────────────────────────────────────────────────
const PhoneFrame = ({ children, label }: { children: React.ReactNode; label?: string }) => (
  <div className="relative" style={{ width: 320, height: 660 }}>
    <div className="absolute inset-0 rounded-[44px] bg-ink-900 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.7),0_0_0_1px_rgba(255,255,255,0.08)] p-[10px]">
      <div className="absolute inset-0 rounded-[44px] pointer-events-none" style={{ boxShadow: "inset 0 0 0 2px rgba(255,255,255,0.04)" }} />
      <div className="relative w-full h-full rounded-[36px] overflow-hidden bg-white">
        <div className="absolute top-0 left-0 right-0 h-10 px-6 flex items-center justify-between text-[12px] font-semibold text-ink-900 z-30">
          <span>9:41</span>
          <div className="absolute left-1/2 -translate-x-1/2 top-1.5 w-24 h-6 rounded-full bg-ink-950" />
          <div className="flex items-center gap-1">
            <span className="w-4 h-2.5 border border-ink-900 rounded-sm relative">
              <span className="absolute inset-[1px] right-1 bg-ink-900 rounded-[1px]" />
            </span>
          </div>
        </div>
        {children}
      </div>
    </div>
    {label && <div className="absolute -bottom-8 left-0 right-0 text-center mono text-[11px] uppercase tracking-[0.2em] text-white/40">{label}</div>}
  </div>
);

const MiniPhone = ({ children, w = 260 }: { children: React.ReactNode; w?: number }) => (
  <div className="relative" style={{ width: w, height: w * 2.05 }}>
    <div className="absolute inset-0 rounded-[36px] bg-ink-900 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.6),0_0_0_1px_rgba(255,255,255,0.08)] p-[8px]">
      <div className="relative w-full h-full rounded-[28px] overflow-hidden bg-white">
        <div className="absolute top-0 left-0 right-0 h-7 px-4 flex items-center justify-between text-[10px] font-semibold text-ink-900 z-30">
          <span>9:41</span>
          <div className="absolute left-1/2 -translate-x-1/2 top-1 w-16 h-4 rounded-full bg-ink-950" />
        </div>
        {children}
      </div>
    </div>
  </div>
);

const HeroPhone = () => {
  const W = 402;
  const H = 874;
  const smScale = 0.425;
  const lgScale = 0.61;
  const smW = Math.round(W * smScale);
  const smH = Math.round(H * smScale);
  const lgW = Math.round(W * lgScale);
  const lgH = Math.round(H * lgScale);
  return (
    <>
      <style>{`
        .hero-phone-outer { width: ${smW}px; height: ${smH}px; }
        .hero-phone-demo { transform: scale(${smScale}); }
        @media (min-width: 1024px) {
          .hero-phone-outer { width: ${lgW}px; height: ${lgH}px; }
          .hero-phone-demo { transform: scale(${lgScale}); }
        }
      `}</style>
      <div className="relative hero-phone-outer mx-auto">
        <div className="absolute z-30 hero-float-payment">
          <div className="floaty" style={{ "--r": "-6deg" } as CSSWithVars}>
            <FloatPayment />
          </div>
        </div>
        <div className="absolute z-30 hero-float-sms">
          <div className="floaty" style={{ "--r": "5deg" } as CSSWithVars}>
            <FloatSMS />
          </div>
        </div>
        <style>{`
          .hero-float-payment { top: 10px; left: -70px; transform-origin: top left; transform: scale(0.494); }
          .hero-float-sms { bottom: 10px; right: -80px; transform-origin: bottom right; transform: scale(0.494); }
          @media (min-width: 1024px) {
            .hero-float-payment { top: 30px; left: -180px; transform: scale(0.65); }
            .hero-float-sms { bottom: 30px; right: -170px; transform: scale(0.65); }
          }
        `}</style>
        <div className="hero-phone-demo origin-top-left">
          <HeroBookingFlowOriginal />
        </div>
        {/* Scroll-takeover payoff. Hidden by default; revealed only while the
            takeover is active (see src/hero-scroll-takeover.css). A real element
            rather than a CSS ::after so the scroll-timeline polyfill, which wires
            up animations via animationstart on real targets, can drive it. */}
        <div className="hero-payoff" aria-hidden="true">Better is here.</div>
      </div>
    </>
  );
};

// ─── FEATURE PHONE MOCKS ──────────────────────────────────────────────────────
const LinkBioMock = () => (
  <MiniPhone w={220}>
    <div className="absolute inset-0 pt-9 px-4 pb-3 bg-gradient-to-b from-figgy-50 to-white">
      <div className="flex flex-col items-center pt-2">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-figgy-500 to-figgy-700 ring-4 ring-white shadow-md flex items-center justify-center text-white font-black text-xl">M</div>
        <div className="mt-2 font-black text-ink-900">@mayacuts</div>
        <div className="text-[10px] text-ink-500">Brooklyn · Booking + Inquiries</div>
      </div>
      <div className="mt-3 space-y-2">
        {[
          { t: "Book a cut", s: "From $35", icon: <ICalendar className="w-4 h-4" /> },
          { t: "Buy a package (3 cuts)", s: "Save $20", icon: <IPackage className="w-4 h-4" /> },
          { t: "Text Maya", s: "Reply in <2hr", icon: <IChat className="w-4 h-4" /> },
          { t: "Tip the chair", s: "Optional", icon: <ICard className="w-4 h-4" /> },
        ].map((r, i) => (
          <div key={i} className={`rounded-xl border ${i===0 ? "border-figgy-500 bg-figgy-50 shadow-[0_0_0_3px_rgba(89,86,246,0.1)]" : "border-ink-200 bg-white"} p-2.5 flex items-center gap-3`}>
            <div className={`w-8 h-8 rounded-lg ${i===0 ? "bg-figgy-500 text-white" : "bg-ink-100 text-ink-600"} flex items-center justify-center`}>{r.icon}</div>
            <div className="flex-1"><div className="text-[12px] font-bold text-ink-900">{r.t}</div><div className="text-[10px] text-ink-500">{r.s}</div></div>
            <IArrow className="w-4 h-4 text-ink-400" />
          </div>
        ))}
      </div>
      <div className="mt-3 mono text-[9px] uppercase tracking-widest text-ink-400 text-center">getfiggy.link/maya</div>
    </div>
  </MiniPhone>
);

const PaymentMock = () => (
  <MiniPhone w={220}>
    <div className="absolute inset-0 pt-9 px-4 bg-white flex flex-col">
      <div className="text-[10px] mono uppercase tracking-widest text-ink-500 mt-2">Apr 16 · 2:00 PM</div>
      <div className="font-black text-ink-900 text-xl mt-1">Pay Maya</div>
      <div className="mt-3 rounded-xl bg-ink-50 p-3 flex items-baseline justify-between">
        <span className="text-[11px] text-ink-500">Signature Cut</span>
        <span className="font-black text-ink-900 text-2xl">$65</span>
      </div>
      <div className="mt-3 space-y-1.5">
        {[
          { t:"Apple Pay", s:"·· 4242 · default", on:true,  icon:<LogoApplePay className="w-4 h-4" /> },
          { t:"Google Pay", s:"Set up",           on:false, icon:<LogoGooglePay className="w-4 h-4" /> },
          { t:"Card",       s:"Enter manually",   on:false, icon:<ICard className="w-4 h-4" /> },
        ].map((m, i) => (
          <div key={i} className={`rounded-xl border p-2.5 flex items-center gap-3 ${m.on ? "border-figgy-500 bg-figgy-50 shadow-[0_0_0_3px_rgba(89,86,246,0.12)]" : "border-ink-200"}`}>
            <div className={`w-8 h-8 rounded-lg ${m.on ? "bg-ink-900 text-white" : "bg-ink-100 text-ink-600"} flex items-center justify-center`}>{m.icon}</div>
            <div className="flex-1"><div className="text-[12px] font-bold text-ink-900">{m.t}</div><div className="text-[10px] text-ink-500">{m.s}</div></div>
            {m.on && <div className="w-4 h-4 rounded-full bg-figgy-500 text-white flex items-center justify-center"><ICheck className="w-3 h-3" stroke={3}/></div>}
          </div>
        ))}
      </div>
      <div className="mt-auto mb-3 rounded-xl bg-ink-900 text-white text-center py-3 font-bold text-sm flex items-center justify-center gap-2 cursor-pulse">
        <LogoApplePay className="w-4 h-4" /> Pay $65
      </div>
    </div>
  </MiniPhone>
);

// ─── FEATURE ANIMATION MOCKS ─────────────────────────────────────────────────

function useRippleCanvas(canvasRef: React.RefObject<HTMLCanvasElement | null>) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const W = canvas.width, H = canvas.height;
    const img = ctx.createImageData(W, H);
    const data = img.data;
    const bayer = [0,8,2,10,12,4,14,6,3,11,1,9,15,7,13,5];
    const back = [226,236,246], front = [194,228,255];
    const cx = W * 0.52, cy = H * 0.30;
    const cx2 = W * 0.16, cy2 = H * 0.94;
    let raf: number;
    const frame = (ts: number) => {
      const t = ts * 0.0009;
      for (let y = 0; y < H; y++) {
        for (let x = 0; x < W; x++) {
          const dx = x - cx, dy = y - cy;
          const d = Math.sqrt(dx * dx + dy * dy);
          let v = 0.5 + 0.5 * Math.sin(d * 0.42 - t * 1.7);
          const dx2 = x - cx2, dy2 = y - cy2;
          const d2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);
          v = v * 0.62 + (0.5 + 0.5 * Math.sin(d2 * 0.30 - t * 1.1)) * 0.38;
          const thr = (bayer[(y & 3) * 4 + (x & 3)] + 0.5) / 16;
          const c = v > thr ? front : back;
          const i = (y * W + x) * 4;
          data[i] = c[0]; data[i+1] = c[1]; data[i+2] = c[2]; data[i+3] = 255;
        }
      }
      ctx.putImageData(img, 0, 0);
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [canvasRef]);
}

const BookingCardAnim = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useRippleCanvas(canvasRef);
  return (
    <div className="relative w-full feat-anim-frame" style={{ borderRadius: 18, overflow: "hidden", background: "#fff", boxShadow: "0 1px 2px rgba(16,18,40,.05),0 22px 48px -20px rgba(16,18,40,.30)" }}>
      <div className="absolute inset-0" style={{ background: "radial-gradient(58% 64% at 50% 30%, rgba(89,86,246,.07), transparent 70%)" }} />
      <div className="absolute" style={{ left: "50%", top: 30, width: 300, animation: "feat-cardZoom 6.5s cubic-bezier(.16,1,.3,1) infinite" }}>
        <div style={{ animation: "feat-floatTiny 4.6s ease-in-out infinite" }}>
          <div className="relative overflow-hidden" style={{ height: 340, borderRadius: 22, outline: "1px solid #B0D4F5", boxShadow: "0 2px 8px rgba(20,45,85,.12),0 24px 54px -20px rgba(20,45,85,.42)", background: "#E2ECF6", maskImage: "linear-gradient(to bottom,#000 75%,transparent 99%)", WebkitMaskImage: "linear-gradient(to bottom,#000 75%,transparent 99%)" }}>
            <canvas ref={canvasRef} width={96} height={128} className="absolute inset-0 w-full h-full" style={{ imageRendering: "pixelated", opacity: 0.92 }} />
            <div className="relative flex flex-col gap-4" style={{ padding: "21px 21px 0" }}>
              <div className="flex items-center gap-3" style={{ animation: "feat-rvL1 6.5s cubic-bezier(.2,.7,.2,1) infinite" }}>
                <div className="flex items-center justify-center font-display font-extrabold" style={{ width: 42, height: 42, borderRadius: 13, background: "#fff", border: "1.5px solid #B0D4F5", color: "#1e3a5f", fontSize: 19 }}>O</div>
                <div>
                  <div className="font-bold" style={{ color: "#1e3a5f", fontSize: 15, letterSpacing: "-0.01em" }}>Oakhart Barber Co.</div>
                  <div style={{ color: "#4a5f7f", fontSize: 12 }}>@oakhartbarber</div>
                </div>
              </div>
              <div style={{ animation: "feat-rvL2 6.5s cubic-bezier(.2,.7,.2,1) infinite" }}>
                <div style={{ color: "#4a5f7f", fontSize: 10.5, letterSpacing: ".16em", fontWeight: 700 }}>SERVICE</div>
                <div className="font-bold" style={{ color: "#1e3a5f", fontSize: 20, letterSpacing: "-0.015em", marginTop: 4 }}>Skin Fade + Beard Trim</div>
                <div style={{ color: "#4a5f7f", fontSize: 13, marginTop: 2 }}>with Marcus</div>
              </div>
              <div className="relative overflow-hidden flex items-center justify-between" style={{ borderRadius: 13, background: "rgba(255,255,255,.55)", border: "1px solid #B0D4F5", padding: "12px 14px", animation: "feat-rvL3 6.5s cubic-bezier(.2,.7,.2,1) infinite" }}>
                <span style={{ color: "#4a5f7f", fontSize: 13, fontWeight: 600 }}>Sat, Jun 20</span>
                <span style={{ color: "#1e3a5f", fontSize: 14, fontWeight: 700 }}>2:30 PM</span>
                <div className="absolute top-0 bottom-0 left-0" style={{ width: "55%", background: "linear-gradient(110deg,transparent 30%,rgba(255,255,255,.85) 50%,transparent 70%)", animation: "feat-sweepOnce 6.5s ease-in-out infinite" }} />
              </div>
            </div>
            <div className="absolute" style={{ top: 18, right: 18, width: 30, height: 30 }}>
              <div className="absolute inset-0 rounded-full" style={{ background: "#1e3a5f", animation: "feat-ringPulse 2.4s ease-out infinite" }} />
              <div className="relative flex items-center justify-center rounded-full font-black" style={{ width: 30, height: 30, background: "#1e3a5f", color: "#C2E4FF", fontSize: 14, animation: "feat-badgePop 6.5s cubic-bezier(.2,.7,.2,1) infinite" }}>✓</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const LinkPreviewAnim = () => (
  <div className="relative w-full feat-anim-frame" style={{ borderRadius: 18, overflow: "hidden", background: "#fff", boxShadow: "0 1px 2px rgba(16,18,40,.05),0 22px 48px -20px rgba(16,18,40,.30)" }}>
    <div className="absolute inset-0 flex items-center justify-center" style={{ padding: 22 }}>
      <div className="flex flex-col items-start gap-[9px]" style={{ width: 300 }}>
        <div style={{ alignSelf: "flex-start", maxWidth: 280, background: "#E9E9EB", color: "#000", borderRadius: "19px 19px 19px 6px", padding: "9px 15px", fontSize: 15, lineHeight: 1.34, fontFamily: "-apple-system,'SF Pro Text',system-ui,sans-serif", transformOrigin: "bottom left", boxShadow: "0 1px 1px rgba(0,0,0,.04)", animation: "feat-msgPop 6.5s cubic-bezier(.2,.8,.2,1) infinite" }}>
          bet, here's the link
        </div>
        <div style={{ alignSelf: "flex-start", width: 300, borderRadius: 18, overflow: "hidden", background: "#fff", boxShadow: "0 8px 24px -10px rgba(0,0,0,.3),0 1px 2px rgba(0,0,0,.06)", transformOrigin: "top left", animation: "feat-prevUnfurl 6.5s cubic-bezier(.16,1,.3,1) infinite" }}>
          <div className="relative overflow-hidden" style={{ height: 140, background: "radial-gradient(120% 120% at 30% 0%,#2e2a24,#100f0d 72%)" }}>
            <div className="absolute" style={{ inset: 10, border: "1px solid rgba(217,182,90,.34)", borderRadius: 10 }} />
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-[5px]">
              <div className="mono" style={{ fontSize: 8, letterSpacing: ".42em", color: "#D9B65A" }}>EST · 2019</div>
              <div className="font-display font-extrabold" style={{ fontSize: 32, letterSpacing: ".02em", color: "#F3EAD6", lineHeight: 1 }}>OAKHART</div>
              <div style={{ width: 30, height: 1, background: "#D9B65A" }} />
              <div className="mono" style={{ fontSize: 8, letterSpacing: ".46em", color: "#C9A24A" }}>BARBER CO.</div>
            </div>
            <div className="absolute flex items-center justify-center rounded-full font-display font-extrabold" style={{ left: 12, bottom: 12, width: 26, height: 26, background: "#D9B65A", color: "#1b1a16", fontSize: 12, animation: "feat-rvA 6.5s cubic-bezier(.2,.7,.2,1) infinite" }}>O</div>
            <div className="absolute top-0 bottom-0 left-0 pointer-events-none" style={{ width: "60%", background: "linear-gradient(110deg,transparent 30%,rgba(255,255,255,.45) 50%,transparent 70%)", animation: "feat-ogSweep 6.5s ease-in-out infinite" }} />
          </div>
          <div style={{ padding: "9px 13px 10px", background: "#fff" }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: "#000", lineHeight: 1.25 }}>Skin Fade + Beard · Oakhart Barber Co.</div>
            <div style={{ fontSize: 11, color: "#8E8E93", marginTop: 2 }}>getfiggy.link</div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const SayLessAnim = () => (
  <div className="relative w-full feat-anim-frame" style={{ borderRadius: 18, overflow: "hidden", background: "#fff", boxShadow: "0 1px 2px rgba(16,18,40,.05),0 22px 48px -20px rgba(16,18,40,.30)" }}>
    <div className="absolute inset-0 flex items-center justify-center" style={{ padding: 22 }}>
      <div className="flex flex-col items-start" style={{ width: 300 }}>
        <div style={{ alignSelf: "flex-start", maxWidth: 290, background: "#E9E9EB", color: "#000", borderRadius: "19px 19px 19px 6px", padding: "9px 14px", fontSize: 15, lineHeight: 1.34, fontFamily: "-apple-system,'SF Pro Text',system-ui,sans-serif", transformOrigin: "bottom left", boxShadow: "0 1px 1px rgba(0,0,0,.04)", animation: "feat-saylessPop 6s cubic-bezier(.2,.8,.2,1) infinite" }}>
          Hey Jordan — say less 😏<br />
          Skin Fade + Beard Trim with Marcus • Sat, Jun 20 at 2:30 PM<br />
          <span style={{ color: "#007AFF" }}>getfiggy.link/r/8x4k2p</span>
        </div>
      </div>
    </div>
  </div>
);

// ─── NAV ──────────────────────────────────────────────────────────────────────
const Nav = () => (
  <nav className="fixed top-0 left-0 right-0 z-40 bg-white/70 dark:bg-ink-950/70 backdrop-blur-xl border-b border-ink-200/70 dark:border-white/5">
    <Container className="flex items-center justify-between h-16">
      <Link to="/"><FiggyLogo wordmarkClassName="text-figgy-500" /></Link>
      <div className="hidden md:flex items-center gap-8 text-sm text-ink-600 dark:text-white/70">
        <a href="#product" className="nav-link hover:text-ink-950 dark:hover:text-white transition">Product</a>
        <a href="#money"   className="nav-link hover:text-ink-950 dark:hover:text-white transition">Make money</a>
        <a href="#compare" className="nav-link hover:text-ink-950 dark:hover:text-white transition">Why Figgy</a>
        <a href="#pricing" className="nav-link hover:text-ink-950 dark:hover:text-white transition">Pricing</a>
      </div>
      <div className="flex items-center gap-2">
        <Link to="/fancy-signin" className="hidden sm:block text-sm text-ink-600 hover:text-ink-950 dark:text-white/70 dark:hover:text-white px-3 py-2 transition">Sign in</Link>
        <Link to="/magic-onboarding-v2" onClick={() => trackCta('Start free', 'nav')} className="bg-[#5048E5] text-white hover:bg-figgy-600 dark:bg-white dark:text-ink-950 dark:hover:bg-figgy-50 text-sm font-bold px-4 py-2 rounded-full transition">Start free</Link>
      </div>
    </Container>
  </nav>
);

// ─── HERO ─────────────────────────────────────────────────────────────────────
const Hero = () => (
  <section className="hero-takeover relative pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden grain">
    <div className="absolute inset-0 hero-glow" />
    <div className="absolute inset-0 dotgrid-ink opacity-40 dark:hidden" />
    <div className="absolute inset-0 dotgrid opacity-40 hidden dark:block" />
    <Container className="relative">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-6 items-center">
        <div className="lg:col-span-7 reveal-soft">
          <SectionLabel num="01" label="FOR BARBERS, STYLISTS & SOLO PROS" />
          <h1 className="mt-5 font-display font-extrabold text-[44px] sm:text-6xl lg:text-[88px] leading-[0.95] tracking-[-0.03em] text-ink-900 dark:text-white text-balance reveal-words">
            <span className="relative inline-block">
              <span className="relative z-10">Your booking page should sell your work—not just show a calendar.</span>
              <span className="absolute inset-x-0 bottom-2 h-3 bg-figgy-500/40 -z-0 skew-x-[-12deg]" />
            </span>
          </h1>
          <p className="mt-6 max-w-xl text-lg md:text-xl text-ink-600 dark:text-white/60 text-pretty leading-relaxed">
            Turn your Instagram traffic into appointments with a faster, premium booking experience built for independent service pros.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/magic-onboarding-v2" onClick={() => trackCta('Create my free page', 'hero_primary')} className="group relative bg-figgy-500 hover:bg-figgy-600 text-white font-bold px-6 py-4 rounded-2xl text-base transition shadow-[0_10px_30px_-10px_rgba(89,86,246,0.6)] shine">
              Create my free page
              <span className="ml-2 inline-block transition-transform group-hover:translate-x-0.5">→</span>
            </Link>
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-[13px]">
            <div className="flex items-center gap-2 text-ink-600 dark:text-white/60"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 dark:bg-emerald-400" />No credit card</div>
            <div className="flex items-center gap-2 text-ink-600 dark:text-white/60"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 dark:bg-emerald-400" />No app for clients</div>
            <div className="flex items-center gap-2 text-ink-600 dark:text-white/60"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 dark:bg-emerald-400" />Keep 99.25%</div>
          </div>
        </div>
        <div className="lg:col-span-5 flex justify-center">
          <HeroPhone />
        </div>
      </div>
    </Container>
  </section>
);

// ─── PROBLEM ──────────────────────────────────────────────────────────────────
const Problem = () => {
  const tools = [
    { t: "Square",    s: "Payments",   c: "from-slate-700 to-slate-900",      logo: "/logos/square.svg" },
    { t: "Calendly",  s: "Booking",    c: "from-sky-500 to-blue-700",         logo: "/logos/calendly.svg" },
    { t: "iMessage",  s: "Texting",    c: "from-green-400 to-green-600",      logo: "/logos/imessage.svg" },
    { t: "Notes",     s: "Clients",    c: "from-amber-400 to-amber-600",      logo: "/logos/apple.svg" },
    { t: "Venmo",     s: "Tips",       c: "from-sky-400 to-cyan-600",         logo: "/logos/venmo.svg" },
    { t: "Sheets",    s: "Revenue",    c: "from-emerald-500 to-emerald-700",  logo: "/logos/googlesheets.svg" },
    { t: "Insta DMs", s: "Inquiries",  c: "from-fuchsia-500 to-purple-700",   logo: "/logos/instagram.svg" },
    { t: "Apple Cal", s: "Schedule",   c: "from-rose-500 to-red-700",         logo: "/logos/googlecalendar.svg" },
  ];
  return (
    <section className="relative py-24 md:py-32 bg-white dark:bg-ink-950 overflow-hidden">
      <Container>
        <SectionLabel num="02" label="The problem" />
        <h2 className="mt-4 font-display font-extrabold text-4xl md:text-6xl text-ink-900 dark:text-white max-w-3xl tracking-[-0.02em] text-balance reveal">
          Running your business shouldn't feel like duct-taping apps together.
        </h2>
      </Container>
      <div className="relative mt-16 overflow-hidden" style={{ maskImage: "linear-gradient(90deg, transparent, black 12%, black 88%, transparent)" }}>
        <div className="flex gap-4 scroll-x w-max">
          {[...tools, ...tools].map((tool, i) => (
            <div key={i} className="rounded-2xl bg-ink-50 dark:bg-white/[0.04] border border-ink-200 dark:border-white/10 px-5 py-4 flex items-center gap-3 min-w-[240px]"
                 style={{ transform: `rotate(${(i % 4 - 1.5)}deg)` }}>
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${tool.c} flex items-center justify-center p-2.5`}><img src={tool.logo} alt={tool.t} className="w-full h-full" /></div>
              <div>
                <div className="text-ink-900 dark:text-white font-bold text-sm">{tool.t}</div>
                <div className="text-ink-500 dark:text-white/40 text-xs mono uppercase tracking-wider">{tool.s}</div>
              </div>
              <IX className="w-4 h-4 text-ink-400 dark:text-white/30 ml-auto" />
            </div>
          ))}
        </div>
      </div>
      <Container className="mt-12">
        <div className="grid md:grid-cols-2 gap-x-12 gap-y-3 max-w-3xl reveal-stagger">
          {["Booking tool in one place","Payments somewhere else","Texting clients manually","No real way to grow revenue"].map((b, i) => (
            <div key={i} className="flex items-center gap-3 py-3 border-b border-ink-200 dark:border-white/10">
              <span className="mono text-[11px] text-ink-400 dark:text-white/30 w-6">0{i+1}</span>
              <IX className="w-4 h-4 text-rose-500 dark:text-rose-400" />
              <span className="text-ink-700 dark:text-white/80 text-base">{b}</span>
            </div>
          ))}
        </div>
        <p className="mt-10 font-display font-extrabold text-2xl md:text-4xl text-ink-900 dark:text-white/90 max-w-2xl tracking-tight">
          You're doing too much<br/>just to stay booked.
        </p>
      </Container>
    </section>
  );
};

// ─── SOLUTION ─────────────────────────────────────────────────────────────────
const Solution = () => {
  const stageRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<HTMLDivElement>(null);
  const [collapsing, setCollapsing] = useState(false);

  const triggerCollapse = useCallback(() => {
    const stage = stageRef.current;
    const target = targetRef.current;
    if (!stage || !target) return;
    const targetRect = target.getBoundingClientRect();
    const tCx = targetRect.left + targetRect.width / 2;
    const tCy = targetRect.top  + targetRect.height / 2;
    stage.querySelectorAll<HTMLElement>(".collapse-box").forEach((box) => {
      const r = box.getBoundingClientRect();
      box.style.setProperty("--tx", `${(tCx - r.left - r.width / 2).toFixed(0)}px`);
      box.style.setProperty("--ty", `${(tCy - r.top  - r.height / 2).toFixed(0)}px`);
    });
    setCollapsing(true);
  }, []);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { setTimeout(triggerCollapse, 450); io.disconnect(); } });
    }, { threshold: 0.55 });
    io.observe(stage);
    return () => io.disconnect();
  }, [triggerCollapse]);

  const scattered = [
    { l: "BOOK",  e: "📅", x: "28%", y: "12%", r: -8 },
    { l: "PAY",   e: "💳", x: "55%", y: "22%", r:  6 },
    { l: "TEXT",  e: "💬", x: "20%", y: "62%", r: 10 },
    { l: "NOTES", e: "📝", x: "58%", y: "60%", r: -4 },
    { l: "TIPS",  e: "💰", x: "40%", y: "38%", r:  2 },
  ];

  return (
    <section className="relative py-24 md:py-32 bg-gradient-to-b from-white via-ink-50 to-white dark:from-ink-950 dark:via-ink-900 dark:to-ink-950">
      <Container>
        <SectionLabel num="03" label="The solution" />
        <h2 className="mt-4 font-display font-extrabold text-4xl md:text-6xl text-ink-900 dark:text-white max-w-3xl tracking-[-0.02em] text-balance reveal">
          Figgy is your business platform.
        </h2>
      </Container>
      <Container className="mt-14">
        <div ref={stageRef} className={`collapse-stage relative rounded-3xl border border-ink-200 dark:border-white/10 bg-ink-50 dark:bg-white/[0.03] p-8 md:p-12 overflow-hidden ${collapsing ? "is-collapsing" : ""}`}>
          <div className="grid md:grid-cols-[1fr_auto_1fr] items-center gap-8">
            <div className="relative h-56">
              {scattered.map((c, i) => (
                <div key={i} className="collapse-box absolute rounded-xl bg-white dark:bg-white/[0.06] border border-ink-200 dark:border-white/10 px-3 py-2 mono text-[11px] text-ink-700 dark:text-white/70 uppercase tracking-widest flex items-center gap-1.5"
                     style={{ left: c.x, top: c.y, transform: `rotate(${c.r}deg)`, "--r": `${c.r}deg`, animationDelay: `${i * 0.08}s` } as CSSWithVars}>
                  <span className="text-sm leading-none">{c.e}</span>
                  {c.l}
                </div>
              ))}
            </div>
            <div className="flex md:flex-col items-center justify-center gap-2 text-figgy-600 dark:text-figgy-400">
              <div className="mono text-[10px] uppercase tracking-widest text-ink-500 dark:text-white/40 hidden md:block">Collapses into</div>
              <IArrow className="w-8 h-8 hidden md:block" />
              <IArrowDown className="w-8 h-8 md:hidden" />
            </div>
            <div className="flex justify-center">
              <div ref={targetRef} className="collect-target relative w-full max-w-xs rounded-2xl bg-gradient-to-br from-figgy-500 to-figgy-700 p-6 shadow-[0_30px_60px_-20px_rgba(89,86,246,0.6)]">
                <img src="/uploads/figgy_icon_transparent.png" alt="Figgy" width={28} height={28} style={{ objectFit: "contain" }} />
                <div className="mt-4 text-white font-display font-extrabold text-2xl tracking-tight">getfiggy.link/maya</div>
                <div className="mt-2 text-white/70 text-sm">One link. Everything.</div>
                <div className="mt-5 grid grid-cols-3 gap-2">
                  {["Book","Pay","Text"].map(t => (
                    <div key={t} className="rounded-xl bg-white/15 backdrop-blur text-white text-center py-2 text-xs font-bold">{t}</div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

// ─── FEATURES ─────────────────────────────────────────────────────────────────
const Features = () => {
  const features = [
    { id: "booking", num: "01", title: "Make every booking feel like a big deal.", body: "Clients get a clean, branded booking experience from start to finish.", mock: <BookingCardAnim /> },
    { id: "link", num: "02", title: "Send links people actually tap.", body: "Your booking link turns into a branded preview that stands out in texts and DMs.", mock: <LinkPreviewAnim /> },
    { id: "sms", num: "03", title: "Booked. Confirmed. Done.", body: "The second someone books, they get all the details automatically. No extra messages needed.", mock: <SayLessAnim /> },
  ];
  return (
    <section id="product" className="relative py-24 md:py-32 bg-ink-950 text-white">
      <Container>
        <SectionLabel num="04" label="The Experience" variant="adaptive" />
        <h2 className="mt-4 font-display font-extrabold text-4xl md:text-6xl max-w-3xl tracking-[-0.02em] text-balance reveal text-white">
          The little things that make you legit.
        </h2>
        <div className="mt-20 space-y-20 md:space-y-28">
          {features.map((f, i) => (
            <div key={f.id} className={`grid md:grid-cols-2 gap-12 items-center ${i % 2 ? "md:[&>*:first-child]:order-2" : ""}`}>
              <div className="reveal">
                <div className="mono text-[11px] text-figgy-400 uppercase tracking-[0.2em] font-bold">{f.num}</div>
                <h3 className="mt-4 font-display font-extrabold text-3xl md:text-5xl tracking-tight text-balance text-white">{f.title}</h3>
                <p className="mt-5 text-white/50 text-lg leading-relaxed max-w-md">{f.body}</p>
              </div>
              <div className="flex justify-center reveal-scale">
                {f.mock}
              </div>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
};

// ─── MONEY ────────────────────────────────────────────────────────────────────
const Money = () => (
  <section id="money" className="relative py-24 md:py-32 bg-figgy-500 text-white overflow-hidden">
    <div className="absolute inset-0 dotgrid opacity-20" />
    <Container className="relative">
      <SectionLabel num="05" label="Make money" variant="onColor" />
      <div className="mt-6 grid lg:grid-cols-[1fr_auto] gap-12 items-end">
        <div>
          <h2 className="font-display font-extrabold text-4xl md:text-7xl max-w-4xl tracking-[-0.03em] text-balance reveal">
            Turn empty slots into more income.
          </h2>
          <p className="mt-6 text-white/70 text-lg md:text-xl max-w-xl leading-relaxed">
            Deposits cut no-shows. Smart rebooking brings clients back. Packages increase what each client spends.
          </p>
        </div>
        <div className="rounded-3xl bg-white text-ink-950 p-7 min-w-[270px] shadow-2xl reveal-scale">
          <div className="mono text-[10px] uppercase tracking-widest text-ink-400">This month</div>
          <div className="mt-3 font-display font-extrabold text-5xl tracking-tight">+$1,240</div>
          <div className="mt-2 text-sm text-emerald-600 font-bold">↑ 28% more booked revenue</div>
          <div className="mt-6 space-y-3">
            {[['Deposits collected','$480'],['Rebooks','$510'],['Packages','$250']].map(([a,b]) => (
              <div key={a} className="flex justify-between text-sm border-t border-ink-100 pt-3"><span className="text-ink-500">{a}</span><b>{b}</b></div>
            ))}
          </div>
        </div>
      </div>
    </Container>
  </section>
);

// ─── COMPARE ──────────────────────────────────────────────────────────────────
const Compare = () => {
  const rows = [
    ['Branded link-in-bio booking page', true, false, false],
    ['Built-in payments + deposits', true, true, false],
    ['SMS reminders + smart rebooking', true, false, false],
    ['Built for solo service providers', true, false, false],
    ['No subscription to start', true, false, true],
    ['Revenue growth tools', true, false, false],
  ];
  return (
    <section id="compare" className="relative py-24 md:py-32 bg-white dark:bg-ink-950 text-ink-900 dark:text-white">
      <Container>
        <SectionLabel num="06" label="Why Figgy" />
        <h2 className="mt-4 font-display font-extrabold text-4xl md:text-6xl max-w-3xl tracking-[-0.02em] text-balance reveal text-ink-900 dark:text-white">
          Not another scheduler.
        </h2>
        <div className="mt-14 overflow-x-auto reveal">
          <div className="min-w-[640px] rounded-3xl border border-ink-200 dark:border-white/10 overflow-hidden">
            <div className="grid grid-cols-[1fr_120px_120px_120px] bg-ink-50 dark:bg-white/[0.04] text-sm font-bold">
              <div className="p-5"></div>
              <div className="p-5 text-center text-figgy-600 dark:text-figgy-300">Figgy</div>
              <div className="p-5 text-center text-ink-500 dark:text-white/50">Square</div>
              <div className="p-5 text-center text-ink-500 dark:text-white/50">Calendly</div>
            </div>
            {rows.map(([label, figgy, square, calendly]) => (
              <div key={String(label)} className="grid grid-cols-[1fr_120px_120px_120px] border-t border-ink-200 dark:border-white/10 items-center">
                <div className="p-5 text-ink-700 dark:text-white/75 text-sm">{label as string}</div>
                {[figgy, square, calendly].map((yes, i) => (
                  <div key={i} className="p-5 flex justify-center">
                    {yes ? <ICheck className={`w-5 h-5 ${i === 0 ? 'text-figgy-600 dark:text-figgy-300' : 'text-ink-400 dark:text-white/40'}`} stroke={2.5} /> : <IX className="w-5 h-5 text-ink-300 dark:text-white/20" />}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
};

// ─── PRICING ──────────────────────────────────────────────────────────────────
const Pricing = () => (
  <section id="pricing" className="relative py-24 md:py-32 bg-white text-ink-950">
    <Container>
      <SectionLabel num="07" label="Pricing" variant="ink" />
      <h2 className="mt-4 font-display font-extrabold text-4xl md:text-6xl max-w-3xl tracking-[-0.02em] text-balance reveal">
        Everything you need is free.
      </h2>
      <p className="mt-5 max-w-2xl text-lg text-ink-500 leading-relaxed reveal">
        Run your business with Figgy for free. Upgrade to Pro only when you want unlimited custom booking-page designs created by a Figgy designer.
      </p>
      <div className="mt-14 grid lg:grid-cols-2 gap-5 max-w-4xl reveal-stagger">
        <div className="rounded-3xl border border-ink-200 p-7 flex flex-col">
          <div className="mono text-[11px] uppercase tracking-widest text-ink-500">Free</div>
          <div className="mt-4 flex items-end gap-1">
            <span className="font-display font-extrabold text-5xl">$0</span>
            <span className="text-ink-500 pb-1">/ month</span>
          </div>
          <div className="text-ink-500 text-sm mt-2">
            Everything you need to book, get paid, and bring clients back.
          </div>
          <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm text-ink-700 flex-1">
            {['Booking link','Services menu','Appointment scheduling','Deposits + payments','SMS reminders','Client confirmations','Google Calendar sync','Revenue insights','Rebooking tools','Link-in-bio page'].map(t => (
              <li key={t} className="flex gap-2"><ICheck className="w-4 h-4 text-figgy-600 shrink-0" />{t}</li>
            ))}
          </ul>
          <Link to="/magic-onboarding-v2" onClick={() => trackCta('Start free', 'pricing_free')} className="mt-8 rounded-xl border border-ink-200 text-center font-bold py-3.5 hover:bg-ink-50 transition">Start free</Link>
        </div>
        <div className="relative rounded-3xl bg-ink-950 text-white p-7 flex flex-col shadow-2xl lg:-translate-y-3">
          <div className="absolute top-0 right-7 -translate-y-1/2 rounded-full bg-figgy-500 px-3 py-1 mono text-[10px] uppercase tracking-widest font-bold">Designer made</div>
          <div className="mono text-[11px] uppercase tracking-widest text-white/50">Pro</div>
          <div className="mt-4 flex items-end gap-1">
            <span className="font-display font-extrabold text-5xl">$19</span>
            <span className="text-white/50 pb-1">/ month</span>
          </div>
          <div className="text-white/55 text-sm mt-2">
            Unlimited custom designs by a Figgy designer.
          </div>
          <ul className="mt-8 space-y-3 text-sm text-white/80 flex-1">
            {['Everything in Free','Unlimited design requests','Brand-matched booking pages','Seasonal + campaign refreshes','Priority design turnaround'].map(t => (
              <li key={t} className="flex gap-2"><ICheck className="w-4 h-4 text-figgy-300 shrink-0" />{t}</li>
            ))}
          </ul>
          <Link to="/magic-onboarding-v2" onClick={() => trackCta('Get custom designs', 'pricing_pro')} className="mt-8 rounded-xl bg-figgy-500 hover:bg-figgy-600 text-white text-center font-bold py-3.5 transition">Get custom designs</Link>
        </div>
      </div>
    </Container>
  </section>
);

// ─── FINAL CTA ────────────────────────────────────────────────────────────────
const FinalCTA = () => (
  <section className="relative py-24 md:py-36 bg-white dark:bg-ink-950 text-ink-900 dark:text-white overflow-hidden">
    <div className="absolute inset-0 hero-glow" />
    <Container className="relative text-center">
      <h2 className="font-display font-extrabold text-4xl md:text-7xl tracking-[-0.03em] text-balance max-w-4xl mx-auto reveal text-ink-900 dark:text-white">
        Your next booking could start with one link.
      </h2>
      <p className="mt-6 text-ink-600 dark:text-white/55 text-lg max-w-xl mx-auto">Create your Figgy link and turn followers into paying clients.</p>
      <Link to="/magic-onboarding-v2" onClick={() => trackCta('Create my link', 'final_cta')} className="mt-10 inline-flex items-center gap-2 rounded-2xl bg-figgy-500 hover:bg-figgy-600 px-8 py-4 font-bold text-lg transition shine">
        Create my link <IArrow className="w-5 h-5" />
      </Link>
    </Container>
  </section>
);

// ─── FOOTER ───────────────────────────────────────────────────────────────────
const Footer = () => (
  <footer className="bg-white dark:bg-ink-950 border-t border-ink-200 dark:border-white/10 text-ink-500 dark:text-white/45 text-sm py-8">
    <Container>
      <div className="flex flex-col sm:flex-row gap-5 justify-between items-center">
        <div className="flex items-center gap-3"><FiggyLogo wordmarkClassName="text-figgy-500" /><span className="text-ink-400 dark:text-white/25">© 2026 Figgy</span></div>
        <div className="flex gap-6">
          <Link to="/legal/terms-and-conditions" className="hover:text-ink-800 dark:hover:text-white/70 transition">Terms</Link>
          <Link to="/legal/privacy-policy"       className="hover:text-ink-800 dark:hover:text-white/70 transition">Privacy</Link>
          <Link to="/legal/cookie-policy"        className="hover:text-ink-800 dark:hover:text-white/70 transition">Cookies</Link>
        </div>
      </div>
    </Container>
  </footer>
);

// ─── SCROLL REVEAL HOOK ───────────────────────────────────────────────────────
function useScrollReveal() {
  useEffect(() => {
    const prefersReduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const SELECTOR = ".reveal, .reveal-soft, .reveal-scale, .reveal-stagger, .reveal-words";

    function splitWords(scope: Element | Document) {
      (scope === document ? document : scope as Element).querySelectorAll(".reveal-words").forEach(el => {
        if ((el as HTMLElement).dataset.split) return;
        (el as HTMLElement).dataset.split = "1";
        const walk = (node: Node) => {
          if (node.nodeType === 3) {
            const frag = document.createDocumentFragment();
            const parts = (node.textContent || "").split(/(\s+)/);
            parts.forEach(p => {
              if (/^\s+$/.test(p)) {
                frag.appendChild(document.createTextNode(p));
              } else if (p.length) {
                const span = document.createElement("span");
                span.className = "word";
                span.textContent = p;
                frag.appendChild(span);
              }
            });
            node.parentNode?.replaceChild(frag, node);
          } else if (node.nodeType === 1 && !(node as Element).classList.contains("word")) {
            [...node.childNodes].forEach(walk);
          }
        };
        [...el.childNodes].forEach(walk);
        el.querySelectorAll<HTMLElement>(".word").forEach((w, i) => { w.style.transitionDelay = `${i * 0.05}s`; });
      });
    }

    const seen = new WeakSet<Element>();
    let io: IntersectionObserver | null = null;

    function revealEl(el: Element) { el.classList.add("is-visible"); }

    function registerEl(el: Element) {
      if (seen.has(el)) return;
      seen.add(el);
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight;
      if (r.bottom < vh * 1.1 && r.top < vh * 1.1) { revealEl(el); return; }
      io?.observe(el);
    }

    function scan(scope: Element | Document) {
      splitWords(scope);
      (scope === document ? document : scope as Element).querySelectorAll(SELECTOR).forEach(registerEl);
    }

    if (prefersReduce) {
      document.querySelectorAll(SELECTOR).forEach(revealEl);
      return;
    }

    io = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { revealEl(e.target); io?.unobserve(e.target); } });
    }, { threshold: 0.15, rootMargin: "0px 0px -8% 0px" });

    scan(document);

    const mo = new MutationObserver((muts) => {
      for (const m of muts) {
        m.addedNodes.forEach(n => {
          if (n.nodeType !== 1) return;
          const el = n as Element;
          if (el.matches?.(SELECTOR)) registerEl(el);
          if (el.querySelectorAll) { splitWords(el); el.querySelectorAll(SELECTOR).forEach(registerEl); }
        });
      }
    });
    mo.observe(document.getElementById("root") || document.body, { childList: true, subtree: true });

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const vh = window.innerHeight;
        document.querySelectorAll<Element>(SELECTOR).forEach(el => {
          if (el.classList.contains("is-visible")) return;
          const r = el.getBoundingClientRect();
          if (r.top < vh * 0.92 && r.bottom > 0) { revealEl(el); io?.unobserve(el); }
        });
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    // Parallax
    const onMouseMove = (ev: MouseEvent) => {
      const stages = document.querySelectorAll<HTMLElement>(".parallax-stage");
      if (!stages.length) return;
      const x = (ev.clientX / window.innerWidth - 0.5) * 2;
      const y = (ev.clientY / window.innerHeight - 0.5) * 2;
      stages.forEach(s => { s.style.setProperty("--px", x.toFixed(3)); s.style.setProperty("--py", y.toFixed(3)); });
    };
    window.addEventListener("mousemove", onMouseMove, { passive: true });

    return () => {
      io?.disconnect();
      mo.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("mousemove", onMouseMove);
    };
  }, []);
}

// ─── TIME-BASED THEME ─────────────────────────────────────────────────────────
// Landing-page scoped: daytime (07:00–18:59) = light, nighttime (19:00–06:59) = dark.
// No localStorage, no global document theme — purely derived from the user's clock.
type LandingTheme = "light" | "dark";

const getTimeTheme = (): LandingTheme => {
  const hour = new Date().getHours();
  return hour >= 7 && hour < 19 ? "light" : "dark";
};

// Initializes on mount and re-checks periodically so the theme flips if the user
// leaves the page open across the 7am/7pm boundary.
function useTimeTheme(checkIntervalMs = 60_000): LandingTheme {
  const [theme, setTheme] = useState<LandingTheme>(getTimeTheme);
  useEffect(() => {
    const id = setInterval(() => {
      setTheme((prev) => {
        const next = getTimeTheme();
        return next === prev ? prev : next;
      });
    }, checkIntervalMs);
    return () => clearInterval(id);
  }, [checkIntervalMs]);
  return theme;
}

// ─── PAGE ─────────────────────────────────────────────────────────────────────
const Index = () => {
  useScrollReveal();
  const theme = useTimeTheme();
  // Load the scroll-driven hero takeover only when the landing page mounts, so
  // the polyfill never ships to onboarding/sign-in/dashboard routes.
  useEffect(() => { void enableHeroScrollTakeover(); }, []);
  return (
    <div className={theme === "dark" ? "dark" : ""}>
      <div className="bg-white dark:bg-ink-950 text-ink-900 dark:text-ink-50" style={{ overflowX: "hidden", WebkitFontSmoothing: "antialiased" }}>
      <Nav />
      <main>
        <Hero />
        <Problem />
        <Solution />
        <Features />
        <Money />
        <Compare />
        <Pricing />
        <FinalCTA />
      </main>
      <Footer />
      </div>
    </div>
  );
};

export default Index;
